import type { UserRole } from '../types';

/**
 * Single source of truth for top-level application routes.
 * Each entry defines path and associated access permissions.
 */
export const ROUTES = {
  home: '/',
  clientPortal: '/client-portal',
  operations: '/operations',
  accounting: '/accounting',
  dashboard: '/dashboard',
  quote: '/quote',
  commissions: '/commissions',
  billingReport: '/billing-report',
  settings: '/settings',
  audit: '/audit',
  clients: '/clients',
  salesReps: '/sales-reps',
  users: '/users',
  backup: '/backup',
  profile: '/profile',
  notifications: '/notifications',
  quoteApproval: '/quote-approval/:id',
  mailbox: '/mailbox',
  pickups: '/pickups',
  cases: '/cases',
  partners: '/partners',
  agencies: '/agencies',
  agencyPortal: '/agency-portal',
  whatsappAgent: '/whatsapp-agent',
  customerFollowup: '/customer-followup',
  myWhatsappChats: '/my-whatsapp-chats',
} as const;

/** Legacy Spanish URL paths → canonical English paths (for bookmarks and external links). */
export const LEGACY_ROUTE_REDIRECTS: Record<string, string> = {
  '/portal_clientes': ROUTES.clientPortal,
  '/configuracion': ROUTES.settings,
  '/auditoria': ROUTES.audit,
  '/clientes': ROUTES.clients,
  '/comerciales': ROUTES.salesReps,
  '/usuarios': ROUTES.users,
  '/perfil': ROUTES.profile,
  '/notificaciones': ROUTES.notifications,
  '/buzon': ROUTES.mailbox,
  '/recolecciones': ROUTES.pickups,
  '/casos': ROUTES.cases,
  '/billing_report': ROUTES.billingReport,
};

export const ALL_ROLES: UserRole[] = ['adminapp', 'admin', 'ceo', 'accounting', 'sales_rep', 'operations', 'client', 'agencia_operador'];

export const ROUTE_PERMISSIONS: Record<keyof typeof ROUTES, UserRole[]> = {
  home: ALL_ROLES,
  clientPortal: ['adminapp', 'admin', 'client'],
  operations: ['adminapp', 'admin', 'ceo', 'sales_rep', 'operations'],
  accounting: ['adminapp', 'admin', 'ceo', 'accounting'],
  dashboard: ['adminapp', 'admin', 'ceo', 'accounting'],
  quote: ['adminapp', 'admin', 'ceo', 'sales_rep', 'operations'],
  commissions: ['adminapp', 'admin', 'ceo', 'accounting', 'sales_rep'],
  billingReport: ['adminapp', 'admin', 'ceo', 'accounting'],
  settings: ['adminapp'],
  audit: ['adminapp', 'admin', 'ceo'],
  clients: ['adminapp', 'admin', 'ceo', 'accounting'],
  salesReps: ['adminapp', 'admin', 'ceo', 'accounting'],
  users: ['adminapp', 'admin'],
  backup: ['adminapp', 'admin'],
  profile: ALL_ROLES,
  notifications: ALL_ROLES,
  quoteApproval: [],
  mailbox: ['adminapp', 'admin', 'operations', 'accounting'],
  pickups: ['adminapp', 'admin', 'ceo', 'operations'],
  cases: ['adminapp', 'admin', 'ceo', 'operations', 'accounting'],
  partners: ['adminapp', 'admin', 'ceo'],
  agencies: ['adminapp', 'admin', 'ceo'],
  whatsappAgent: ['adminapp', 'admin'],
  customerFollowup: ['adminapp', 'admin', 'ceo', 'sales_rep'],
  myWhatsappChats: ['adminapp', 'admin', 'sales_rep'],
  agencyPortal: ['adminapp', 'admin', 'agencia_operador'],
};

/** Nested routes for Client Portal. */
export const PORTAL_ROUTES = {
  today: 'today',
  newQuote: 'new-quote',
  quoteHistory: 'quote-history',
  operationHistory: 'operation-history',
  tracking: 'tracking',
  pendingBalance: 'pending-balance',
  myDocuments: 'my-documents',
} as const;

/** Legacy portal sub-path redirects. */
export const LEGACY_PORTAL_ROUTE_REDIRECTS: Record<string, string> = {
  nueva: PORTAL_ROUTES.newQuote,
  'historial-cotizaciones': PORTAL_ROUTES.quoteHistory,
  'historial-operaciones': PORTAL_ROUTES.operationHistory,
  seguimiento: PORTAL_ROUTES.tracking,
  'saldo-pendiente': PORTAL_ROUTES.pendingBalance,
  'mis-documentos': PORTAL_ROUTES.myDocuments,
};

/** Nested routes for Executive Business Vision (Billing Report). */
export const BILLING_ROUTES = {
  dashboard: 'dashboard',
  billing: 'billing',
  rankings: 'rankings',
  control: 'control',
} as const;

/** Legacy billing sub-path redirects. */
export const BILLING_ROUTES_LEGACY: Record<string, string> = {
  colombia: BILLING_ROUTES.billing,
  china: BILLING_ROUTES.billing,
  usa: BILLING_ROUTES.billing,
  guias: BILLING_ROUTES.billing,
  resumen: BILLING_ROUTES.dashboard,
  analisis: BILLING_ROUTES.dashboard,
  'top-clientes': BILLING_ROUTES.rankings,
  comerciales: BILLING_ROUTES.rankings,
  margen: BILLING_ROUTES.control,
  auditoria: BILLING_ROUTES.control,
  facturacion: BILLING_ROUTES.billing,
};

/** Best-effort route key from the current pathname (for view-as redirects). */
export function resolveRouteKeyFromPathname(pathname: string): keyof typeof ROUTES | null {
  if (pathname === ROUTES.home) return 'home';
  if (pathname.startsWith(`${ROUTES.clientPortal}/`) || pathname === ROUTES.clientPortal) return 'clientPortal';

  const entries = (Object.entries(ROUTES) as [keyof typeof ROUTES, string][])
    .filter(([key]) => key !== 'home' && key !== 'clientPortal')
    .sort((a, b) => b[1].length - a[1].length);

  for (const [key, path] of entries) {
    if (path.includes(':')) {
      const base = path.split('/:')[0]!;
      if (pathname === base || pathname.startsWith(`${base}/`)) return key;
      continue;
    }
    if (pathname === path || pathname.startsWith(`${path}/`)) return key;
  }

  return null;
}
